import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import type { WriteResult } from '../hooks/useCostWriteQueue'
import { describeCostControlError } from '../lib/errors'
import { formatMoney } from '../lib/money'
import type { CostContract, CostTenderAward } from '../types'
import FromTenderListSkeleton from './FromTenderListSkeleton'

interface Props {
  open: boolean
  projectId: string
  currency: string
  locale: string
  onOpenChange: (open: boolean) => void
  create: (tenderId: string) => Promise<WriteResult<{ createCostContractFromTender: { contract: CostContract; awardedAmount: string | null; itemCount: number } }>>
  onCreated: (contractId: string, awardedAmount: string | null, itemCount: number, lineCount: number) => void
}

const FromTenderDialog: React.FC<Props> = ({ open, projectId, currency, locale, onOpenChange, create, onCreated }) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery<{ costTenderAwards: CostTenderAward[] }>(Query.COST_TENDER_AWARDS, {
    variables: { projectId },
    skip: !open,
    fetchPolicy: 'network-only',
    context: { handlesErrors: true },
  })
  const [selected, setSelected] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  useEffect(() => {
    if (open) { setSelected(null); setSubmitting(false) }
  }, [open])

  const awards = data?.costTenderAwards
  const submit = async () => {
    if (!selected || submitting) return
    setSubmitting(true)
    const res = await create(selected)
    setSubmitting(false)
    if (res.ok) {
      const { contract, awardedAmount, itemCount } = res.data.createCostContractFromTender
      onOpenChange(false)
      onCreated(contract.id, awardedAmount, itemCount, contract.lineCount)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('CostControl_tender_dialog_title')}</DialogTitle>
          <DialogDescription>{t('CostControl_tender_dialog_hint')}</DialogDescription>
        </DialogHeader>
        {!awards && loading && <FromTenderListSkeleton />}
        {!awards && error && <p className="text-sm text-destructive">{describeCostControlError(error.message, t)}</p>}
        {awards && awards.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_tender_none')}</p>}
        {awards && awards.length > 0 && (
          <RadioGroup value={selected ?? ''} onValueChange={setSelected} className="max-h-[50vh] space-y-2 overflow-y-auto">
            {awards.map((a) => {
              const taken = !!a.contractId
              return (
                <label
                  key={a.tenderId}
                  className={cn('flex items-start gap-3 rounded-md border p-3', taken ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-accent')}
                >
                  <RadioGroupItem value={a.tenderId} disabled={taken} className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{a.tenderName}</div>
                    <div className="text-sm text-muted-foreground">{a.bidderName}</div>
                    {a.nodePath && <p className="text-xs text-muted-foreground">{t('CostControl_tender_budget_item', { path: a.nodePath })}</p>}
                    {taken && <div className="text-xs text-muted-foreground">{t('CostControl_tender_has_contract')}</div>}
                  </div>
                  <div className="shrink-0 text-right text-sm tabular-nums">
                    {a.amount ? formatMoney(a.amount, { locale, currency }) : <span className="text-muted-foreground">{t('CostControl_tender_no_price')}</span>}
                  </div>
                </label>
              )
            })}
          </RadioGroup>
        )}
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
          <Button onClick={submit} disabled={!selected || submitting}>
            {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('CostControl_tender_create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default FromTenderDialog
